/**
 * Users settings page — workspace admin view of every member.
 *
 * Scope (locked 2026-07-21 in the settings pass, tight variant):
 *   - Table columns: User (avatar + name + '(you)' tag), Email, Last active,
 *     Joined, Account role (inline dropdown).
 *   - Actions: change role, remove. Both hidden on the workspace admin's
 *     own row (backend rejects both anyway; frontend just doesn't render
 *     dead buttons).
 *   - "New User" button opens the existing ManageMembersDialog (invite flow
 *     already tested and covers autocomplete + role select + email/copy).
 *
 * Explicitly out of scope (deferred to Phase 2 — see [[project-phase2-backlog]]):
 *   - Pending invites merged into this list (needs a union endpoint or
 *     second fetch). Today invites live under a separate PENDING table and
 *     don't turn into a users row until acceptance.
 *   - Revoke pending invite (no backend endpoint yet).
 *   - New Comment Only / View Only role tiers (needs permission-matrix
 *     re-audit across every gated route).
 *   - Status column (would just say "Active" for everyone until pending
 *     invites are merged — noise).
 */

import { useState } from "react";
import { MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ManageMembersDialog } from "@/components/members/manage-members-dialog";
import { SettingsPageHeader } from "@/pages/settings/settings-placeholder";
import { useAuth } from "@/hooks/use-auth";
import { Input } from "@/components/ui/input";
import {
  useMembers,
  useRemoveMember,
  useRestoreMember,
  useDeleteMemberPermanent,
  useUpdateMemberRole,
  type WorkspaceMember,
} from "@/hooks/use-members";
import {
  INVITABLE_ROLES,
  roleDisplayName,
  type InvitableRole,
} from "@/lib/api-types";
import { ApiError } from "@/lib/api";

function initialsFrom(name: string | undefined | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatDate(value: string | undefined | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Compact "3 minutes ago" / "2 days ago" for the Last active column. */
function timeAgo(value: string | undefined | null): string {
  if (!value) return "Never";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? "" : "s"} ago`;
}

export function UsersSettingsPage() {
  const { user } = useAuth();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [showSuspended, setShowSuspended] = useState(false);
  const membersQuery = useMembers();

  const allMembers = membersQuery.data ?? [];
  // Suspended (soft-removed) members are pulled OUT of the main list — a removed
  // user should not show up alongside active ones. They live in a collapsed
  // "Suspended" section below purely so an admin can restore them.
  const suspendedMembers = allMembers.filter((m) => m.suspendedAt);
  const activeMembers = allMembers.filter((m) => !m.suspendedAt);

  // GET /admin/editors returns workspace members but NOT the admin themselves.
  // We stitch the admin (current user, role='ADMIN') on top as the "Owner" row.
  // If the current user is not ADMIN, they wouldn't reach this route at all
  // (SettingsSidebar hides the Admin section for non-ADMIN).
  const rows: WorkspaceMember[] = [
    ...(user
      ? [
          {
            id: user.id,
            name: user.name || user.email || "Owner",
            email: user.email || "",
            role: "ADMIN",
            createdAt: user.createdAt ?? null,
            // ADMIN's own lastLoginAt isn't on the me payload today; leave it null
            // and render "—". Not worth a schema round-trip for a self-view.
            lastLoginAt: null,
          },
        ]
      : []),
    ...activeMembers,
  ];

  return (
    <div>
      <SettingsPageHeader
        title="Users"
        subtitle="Members of this workspace and their roles."
        actions={
          <Button onClick={() => setInviteOpen(true)} className="h-9">
            New User
          </Button>
        }
      />

      <div className="overflow-x-auto rounded-2xl border border-border bg-background">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">User</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Last active</th>
              <th className="px-4 py-3 font-medium">Joined</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="w-10 px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {membersQuery.isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : membersQuery.isError ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-destructive">
                  Couldn't load members.
                </td>
              </tr>
            ) : (
              rows.map((member) => (
                <UserRow
                  key={member.id}
                  member={member}
                  isSelf={member.id === user?.id}
                />
              ))
            )}
          </tbody>
        </table>
        <div className="border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
          {rows.length} {rows.length === 1 ? "user" : "users"}
        </div>
      </div>

      {suspendedMembers.length > 0 && (
        <div className="mt-6">
          <button
            type="button"
            onClick={() => setShowSuspended((s) => !s)}
            className="mb-2 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            {showSuspended ? "Hide" : "Show"} suspended ({suspendedMembers.length})
          </button>
          {showSuspended && (
            <div className="overflow-x-auto rounded-2xl border border-border bg-background">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">User</th>
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Last active</th>
                    <th className="px-4 py-3 font-medium">Joined</th>
                    <th className="px-4 py-3 font-medium">Role</th>
                    <th className="w-10 px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {suspendedMembers.map((member) => (
                    <UserRow
                      key={member.id}
                      member={member}
                      isSelf={member.id === user?.id}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <ManageMembersDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}

// ── Row ──────────────────────────────────────────────────────────────────────

function UserRow({
  member,
  isSelf,
}: {
  member: WorkspaceMember;
  isSelf: boolean;
}) {
  const [removeOpen, setRemoveOpen] = useState(false);
  const [permanentOpen, setPermanentOpen] = useState(false);
  const restore = useRestoreMember();
  const isAdmin = member.role === "ADMIN";
  const isSuspended = !!member.suspendedAt;
  const roleLocked = isAdmin || isSelf; // can't change ADMIN or self here.
  const actionable = !isAdmin && !isSelf; // has a row action (remove or restore).

  return (
    <tr className={`border-b border-border/60 last:border-b-0 hover:bg-secondary/20 ${isSuspended ? "opacity-60" : ""}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div
            aria-hidden
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-primary/70 to-primary text-xs font-semibold text-primary-foreground"
          >
            {initialsFrom(member.name)}
          </div>
          <div className="min-w-0">
            <div className="truncate font-medium text-foreground">
              {member.name}
              {isSelf && (
                <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                  (you)
                </span>
              )}
              {isSuspended && (
                <span className="ml-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                  Suspended
                </span>
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-muted-foreground">
        <span className="truncate">{member.email}</span>
      </td>
      <td className="px-4 py-3 text-muted-foreground">
        {timeAgo(member.lastLoginAt)}
      </td>
      <td className="px-4 py-3 text-muted-foreground">
        {formatDate(member.createdAt)}
      </td>
      <td className="px-4 py-3">
        {roleLocked ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/50 px-2.5 py-0.5 text-xs font-medium text-foreground">
            {isAdmin ? "Owner" : roleDisplayName(member.role)}
          </span>
        ) : (
          <RoleDropdown member={member} />
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {actionable ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={`Actions for ${member.name}`}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{member.name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {isSuspended ? (
                <DropdownMenuItem
                  disabled={restore.isPending}
                  onSelect={(e) => {
                    e.preventDefault();
                    restore.mutate({ id: member.id });
                  }}
                >
                  {restore.isPending ? "Restoring…" : "Restore access"}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setRemoveOpen(true);
                  }}
                >
                  Suspend user
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={(e) => {
                  e.preventDefault();
                  setPermanentOpen(true);
                }}
              >
                Remove permanently
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
        {actionable && !isSuspended && (
          <RemoveMemberDialog
            open={removeOpen}
            onOpenChange={setRemoveOpen}
            member={member}
          />
        )}
        {actionable && (
          <PermanentDeleteDialog
            open={permanentOpen}
            onOpenChange={setPermanentOpen}
            member={member}
          />
        )}
      </td>
    </tr>
  );
}

// ── Role dropdown ────────────────────────────────────────────────────────────

function RoleDropdown({ member }: { member: WorkspaceMember }) {
  const updateRole = useUpdateMemberRole();
  const currentLabel = roleDisplayName(member.role);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/50 px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors hover:bg-secondary"
          disabled={updateRole.isPending}
        >
          {updateRole.isPending ? "Updating…" : currentLabel}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>Change role</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {INVITABLE_ROLES.map((role) => (
          <DropdownMenuItem
            key={role}
            disabled={role === member.role || updateRole.isPending}
            onSelect={(e) => {
              e.preventDefault();
              if (role === member.role) return;
              updateRole.mutate({ id: member.id, role: role as InvitableRole });
            }}
          >
            {roleDisplayName(role)}
            {role === member.role && (
              <span className="ml-auto text-xs text-muted-foreground">current</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Remove confirmation ──────────────────────────────────────────────────────

function RemoveMemberDialog({
  open,
  onOpenChange,
  member,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  member: WorkspaceMember;
}) {
  const remove = useRemoveMember();
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    remove.mutate(
      { id: member.id },
      {
        onSuccess: () => onOpenChange(false),
        onError: (err) => {
          setError(err instanceof ApiError ? err.message : "Couldn't remove member.");
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Remove {member.name}?</DialogTitle>
          <DialogDescription>
            {member.name} will lose access to this workspace immediately. Their
            uploads, comments, and approval history stay attributed to them, and
            you can restore their access anytime from this list.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p className="text-xs text-destructive">{error}</p>
        )}
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={remove.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={remove.isPending}
          >
            {remove.isPending ? "Suspending…" : "Suspend"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Permanent delete (type-to-confirm) ───────────────────────────────────────

function PermanentDeleteDialog({
  open,
  onOpenChange,
  member,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  member: WorkspaceMember;
}) {
  const remove = useDeleteMemberPermanent();
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const canDelete = confirmText.trim().toLowerCase() === "confirm";

  const close = (v: boolean) => {
    if (!v) { setConfirmText(""); setError(null); }
    onOpenChange(v);
  };

  const submit = () => {
    if (!canDelete) return;
    setError(null);
    remove.mutate(
      { id: member.id },
      {
        onSuccess: () => close(false),
        onError: (err) => {
          setError(err instanceof ApiError ? err.message : "Couldn't remove member.");
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Remove {member.name} permanently?</DialogTitle>
          <DialogDescription>
            This can't be undone. {member.name} is deleted from the workspace and
            won't appear in the Suspended list. Their existing uploads and
            comments stay but lose their author name. The only way to add them
            back is a fresh invite.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-1.5">
          <label htmlFor="confirm-permanent" className="text-xs text-muted-foreground">
            Type <span className="font-semibold text-foreground">confirm</span> to proceed.
          </label>
          <Input
            id="confirm-permanent"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="confirm"
            autoComplete="off"
          />
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => close(false)}
            disabled={remove.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={!canDelete || remove.isPending}
          >
            {remove.isPending ? "Removing…" : "Remove permanently"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
